import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ArenaService } from '../../../services/arenas/arena.service';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';

import { NewsService, NewsArticle } from '../../../services/news/news.service';
import { Arena, RECENT_ARENAS_KEY } from '../../../models/types';

export interface SpadesLeaderboardEntry {
  id: number;
  userId: string;
  nickname: string;
  gameType: string;
  rating: number;
  gamesPlayed: number;
  gamesWon: number;
  gamesLost: number;
  lastPlayed: string;
}

@Component({
  selector: 'app-overview',
  imports: [FormsModule, RouterLink, CommonModule],
  templateUrl: './overview.html',
  styleUrl: './overview.scss',
})
export class Overview implements OnInit {
  private arenaService = inject(ArenaService);
  private newsService = inject(NewsService);
  private http = inject(HttpClient);

  loading = signal(true);
  lobbies = signal<Arena[]>([]);
  searchQuery = signal('');
  activeFilter = signal<'All' | 'Official' | 'Community' | 'Open Slots'>('All');
  viewMode = signal<'grid' | 'list'>('grid');

  leaderboard = signal<SpadesLeaderboardEntry[]>([]);
  leaderboardLoading = signal(true);

  announcements = this.newsService.announcements;

  private getRecentArenasFromLocalStorage(): Arena[] {
    try {
      const stored = localStorage.getItem(RECENT_ARENAS_KEY);
      if (stored) {
        const list = JSON.parse(stored);
        return Array.isArray(list) ? list : [];
      }
    } catch (e) {
      console.error('Failed to parse recent arenas from localStorage', e);
    }
    return [];
  }

  lastPlayedArena = signal<Arena[]>(this.getRecentArenasFromLocalStorage());

  favoriteArenas = signal<Arena[]>([]);

  filteredArenas = computed(() => {
    const query = this.searchQuery().toLowerCase();
    const filter = this.activeFilter();
    const all = this.lobbies();

    return all.filter(arena => {
      const matchesQuery = arena.title.toLowerCase().includes(query) || (arena.description && arena.description.toLowerCase().includes(query));

      let matchesFilter: boolean;
      switch (filter) {
        case 'Official':
          matchesFilter = arena.isOfficial;
          break;
        case 'Community':
          matchesFilter = !arena.isOfficial;
          break;
        case 'Open Slots':
          matchesFilter = arena.onlineUsers < arena.members;
          break;
        default: // 'All'
          matchesFilter = true;
      }

      return matchesQuery && matchesFilter;
    });
  });

  loadLeaderboard() {
    this.http.get<SpadesLeaderboardEntry[]>('https://cards.api.rivalforge.com/api/stats/leaderboard/spades?limit=10').subscribe({
      next: (data) => {
        const sorted = [...data].sort((a, b) => b.rating - a.rating);
        this.leaderboard.set(sorted);
        this.leaderboardLoading.set(false);
      },
      error: (err) => {
        console.error('Failed to load spades leaderboard', err);
        this.leaderboardLoading.set(false);
      }
    });
  }

  ngOnInit(): void {
    this.loadLeaderboard();
    this.arenaService.getAll().subscribe({
      next: (response: Arena[]) => {
        this.lobbies.set(response);
        this.favoriteArenas.set(response);

        const recentStored = this.getRecentArenasFromLocalStorage();
        const updatedRecent = recentStored.map(recent => {
          const liveArena = response.find((lobby: Arena) => lobby.guid === recent.guid);
          return liveArena ? { ...recent, ...liveArena } : recent;
        });
        this.lastPlayedArena.set(updatedRecent);
      },
      error: (error) => {
        console.error(error);
      },
      complete: () => {
        this.loading.set(false);
      }
    })
  }

  setFilter(filter: 'All' | 'Official' | 'Community' | 'Open Slots') {
    this.activeFilter.set(filter);
  }

  getCategoryClass(category: NewsArticle['category']) {
    switch (category) {
      case 'Patch Notes': return 'text-info';
      case 'Esports': return 'text-warning';
      case 'Community': return 'text-success';
      default: return 'text-secondary';
    }
  }
}
